import React, { useState } from 'react';
import {
    NativeSyntheticEvent,
    TextInputKeyPressEventData,
    View,
} from 'react-native';

import { translate } from '../../../services/translate';
import { crmService } from '../../../services/central-api/crm';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { CRMParamList } from '../../../typings/Params';
import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import Card from '../../../components/Card';
import DataTableContainer from '../../../components/DataTableContainer';
import DataTableHeader from '../../../components/DataTableHeader';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import MyDataTable from '../../../components/MyDataTable';
import { Pagination } from '../../../components/Pagination';
import { useCurrentApp } from '../../../context/CurrentApp';
import { openInNewTab } from '../../../services/navigation-assistant';
import { removeDeletedDateFromSerialNo } from '../../../utils/removeDeletedDateFromSerialNo';
import Link from '../../../components/Link';
import { useAuth } from '../../../context/Auth';
import Badge from '../../../components/Badge';
import { maintenanceRequestBadgeColors } from './MaintenanceRequestComponent';
import CameraInformationModal from '../CameraHistory/CameraInformationModal';
import CameraHistoryReportModal from '../CameraHistory/CameraHistoryReportModal';
import Filter from './Filters';

export default function MaintenanceRequests() {
    const navigation = useNavigation<NavigationProp<CRMParamList, 'MaintenanceRequests'>>();
    const route = useRoute<RouteProp<CRMParamList, 'MaintenanceRequests'>>();
    const { isParentApp } = useCurrentApp();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useState<CRMParamList['MaintenanceRequests']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });

    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [selectedMaintenanceRequest, setSelectedMaintenanceRequest] = useState<MaintenanceRequests>();

    const [reportModalIsVisible, setReportModalIsVisible] = useState(false);
    const [cameraInformationModalIsVisible, setCameraInformationModalIsVisible] = useState(false);

    const isFocused = useIsFocused();

    const { data, isLoading } = useQuery({
        queryKey: ['getMaintenanceRequests', filters],
        queryFn: () => crmService.getMaintenanceRequests(filters),
        enabled: isFocused,
        refetchInterval: isFocused ? 30000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter,
                page: 0,
            }));
        }
    }

    function getMaintenanceRequestsStatus(finish: string | null, status?: CameraMaintenanceStatus) {

        if (finish) {
            return <Badge mode='SQUARE' color='green'>{translate('concluded')}</Badge>;
        }
        if (status) {
            const color = maintenanceRequestBadgeColors[status];
            return <Badge mode='SQUARE' color={color}>{translate(status)}</Badge>;
        }
        return <Badge mode='SQUARE' color='orange'>{translate('pending')}</Badge>;
    }

    return (
        <Container>
            <Header
                title={translate('maintenanceRequests')}
            />
            <Card>
                <DataTableHeader
                    title={translate('requestedMaintenances')}
                    zIndex={2}
                    filter={<Filter filters={filters} setFilters={setFilters} />}
                    filterInput={<FilterInput
                        value={textFilter}
                        onChange={setTextFilter}
                        placeholder={translate('typeToSearch')}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        progressPending={isLoading}
                        sortServer={true}
                        onSort={(column, direction) => {
                            navigation.setParams({
                                ...filters,
                                orderBy: column.sortField,
                                orderDirection: direction,
                                page: 1,
                            });

                            setFilters({
                                ...filters,
                                orderBy: column.sortField,
                                orderDirection: direction,
                                page: 0,
                            });
                        }}
                        onRowClicked={(row) => {
                            navigation.navigate('MaintenanceRequestDetail', { id: row.id });
                        }}
                        pointerOnHover
                        columns={[{
                            name: translate('status'),
                            cell: row => <View style={{ minWidth: 120, justifyContent: 'center', alignItems: 'flex-start' }}>
                                {getMaintenanceRequestsStatus(row.finish, row.statusHistory.at(0)?.status)}
                            </View>,
                            maxWidth: '140px',
                            minWidth: '140px',
                        }, {
                            name: 'ID',
                            selector: row => row.id,
                            grow: 0
                        }, {
                            name: translate('company'),
                            selector: row => row.camera.company?.name || '',
                            grow: 2,
                            wrap: true,
                            omit: !isParentApp
                        }, {
                            name: translate('camera'),
                            cell: row => <Link
                                onPress={() => {
                                    setSelectedMaintenanceRequest(row);
                                    setCameraInformationModalIsVisible(true);
                                }}
                                text={row.camera.serialNo ? removeDeletedDateFromSerialNo(row.camera.serialNo) : row.camera.title}
                            />,
                            grow: 2,
                            wrap: true,
                        }, {
                            name: translate('requester'),
                            selector: row => row.requesterUser ? `${row.requesterUser.registry} - ${row.requesterUser.warName || row.requesterUser.name}` : translate('systemGenerated'),
                            grow: 2,
                            wrap: true,
                        }, {
                            name: translate('beginIn'),
                            selector: row => `${moment(row.start).format('DD/MM/YYYY HH:mm:ss')}`,
                            wrap: true,
                            sortable: true,
                            sortField: 'start',
                        }, {
                            name: translate('endIn'),
                            selector: row => row.finish ? `${moment(row.finish).format('DD/MM/YYYY HH:mm:ss')}` : '',
                            wrap: true
                        }, {
                            name: translate('duration'),
                            selector: row => {
                                const finish = row.finish ? moment(row.finish) : moment();
                                const start = moment(row.start);

                                const totalSeconds = Math.max(0, finish.diff(start, 'seconds'));

                                const hours = Math.floor(totalSeconds / 3600);
                                const minutes = Math.floor((totalSeconds % 3600) / 60);
                                const seconds = totalSeconds % 60;

                                const pad = (n: number) => String(n).padStart(2, '0');

                                return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
                            },
                            wrap: true,
                            sortable: true,
                            sortField: 'duration',
                        }, {
                            cell: row => {

                                if (!authenticatedUser?.isAdmin && !authenticatedUser?.permissions.crm_module_access) {
                                    return <></>;
                                }

                                const actions: Action[] = [{
                                    content: translate('viewInformation'),
                                    onPress: () => {
                                        navigation.navigate('MaintenanceRequestDetail', { id: row.id });
                                    }
                                }];

                                if (!row.camera.deletedAt) {
                                    actions.push({
                                        content: translate('openCameraConfigurationOnNewTab'),
                                        onPress: () => {
                                            openInNewTab('CRM', {
                                                screen: 'ManagementDetails', params: {
                                                    id: row.camera.id,
                                                }
                                            });
                                        }
                                    });

                                    actions.push({
                                        content: translate('watchCameraOnNewTab'),
                                        onPress: () => {
                                            openInNewTab('Cameras', {
                                                screen: 'CameraMapPlayer', params: {
                                                    id: row.camera.id,
                                                }
                                            });
                                        },
                                    });
                                }

                                return <DropdownButton
                                    actions={actions}
                                    size={320}
                                    offsetX={-168}
                                />;
                            },
                            wrap: true,
                            button: true
                        }]}
                        data={data?.rows ?? []}
                    />}
                    pagination={<Pagination
                        totalItems={data?.count ?? 0}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>


            <CameraInformationModal
                isVisible={cameraInformationModalIsVisible}
                setIsVisible={setCameraInformationModalIsVisible}
                cameraHistory={selectedMaintenanceRequest}
            />

            <CameraHistoryReportModal
                isVisible={reportModalIsVisible}
                setIsVisible={setReportModalIsVisible}
            />
        </Container>
    );
}
